import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { memo, useRef, useState, useCallback } from "react";
import TimezoneDropdown from "../common/ui/TimezoneDropdown";

import type { AuthenticationProps } from "@/shared/types/common.types";
import navbarDarkLogo from "../../../assets/images/navbar_logo.png";
import navbarLightLogo from "../../../assets/images/navbar_light_logo.png";
import CriticalEventsPanel from "@/shared/components/common/ui/CriticalEventsPanel";
import NotificationPanel from "../common/ui/NotificationPanel";
import { useAppSelector } from "@/store/hooks";
import { selectName, selectRole, selectIsCustomer, selectTabPermissions } from "@/store/authSlice";
import { useTheme } from "@/contexts/ThemeContext";

function Navbar({ logout }: AuthenticationProps) {
  const env = import.meta.env.VITE_ENV;

  const name = useAppSelector(selectName);
  const role = useAppSelector(selectRole);
  const isCustomer = useAppSelector(selectIsCustomer);
  const tabPermissions = useAppSelector(selectTabPermissions);

  const hasAlertsAccess = tabPermissions ? "ALERTS" in tabPermissions : false;
  const hasCriticalEventsAccess = !isCustomer;

  const roleMap: Record<string, string> = {
    Admin: "Admin",
    Associate: "Internal",
    Customer: "External",
  };
  const nameUser = name || "Guest";
  const roleLabel = role ? (roleMap[role] ?? role) : "";

  const { theme: activeTheme, toggleTheme: handleThemeToggle } = useTheme();
  const [activeButton, setActiveButton] = useState<string | null>(null);
  const op = useRef<OverlayPanel>(null);
  const mobileMenuOp = useRef<OverlayPanel>(null);

  const getInitials = useCallback((fullName?: string): string => {
    if (!fullName) return "?";
    return fullName
      .trim()
      .split(" ")
      .map((n) => n[0]?.toUpperCase() ?? "")
      .join("");
  }, []);

  const initials = getInitials(name || "");
  const navbarLogo = activeTheme === "light" ? navbarLightLogo : navbarDarkLogo;
  const logoContainerClass =
    activeTheme === "dark"
      ? "flex items-center justify-center w-14 h-10 rounded-md bg-[var(--bg-surface)] flex-shrink-0"
      : "flex items-center justify-center w-14 h-10 rounded-md border border-[var(--border-subtle)] flex-shrink-0";

  const handleClick = useCallback((path: string) => {
    window.open(path, "_blank", "noopener,noreferrer");
  }, []);

  const handleActiveButton = useCallback((value: string) => {
    setActiveButton((prev) => (prev === value ? null : value));
  }, []);

  const buttonOptions = [
    { id: 1, label: "Help", routePath: "/help", icon: "pi pi-question-circle" },
    { id: 2, label: "About", routePath: "/about", icon: "pi pi-info-circle" },
    { id: 3, label: "What's New", routePath: "/whats-new", icon: "pi pi-bell" },
  ];

  const profileActionClass =
    "navbar-user-action w-full flex items-center gap-2 p-button-text p-button-sm rounded-lg transition duration-150";

  /** Sun/Moon segmented pill toggle — matches screenshot design */
  const ThemeTogglePill = (
    <div
      className={`flex items-center rounded-full border p-0.5 gap-0.5 ${
        activeTheme === "light"
          ? "bg-[var(--bg-secondary-nav)] border-[var(--border-default)]"
          : "bg-[var(--bg-elevated)] border-[var(--border-default)]"
      }`}
      role="group"
      aria-label="Theme switcher"
    >
      {/* Sun — light mode */}
      <button
        type="button"
        onClick={() => activeTheme !== "light" && handleThemeToggle()}
        aria-label="Light theme"
        title="Light theme"
        className={`flex cursor-pointer items-center justify-center w-[30px] h-[30px] rounded-full transition-all duration-150 ${
          activeTheme === "light"
            ? "bg-[var(--bg-light)] border border-[var(--border-default)] text-[var(--text-primary)]"
            : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
        }`}
        style={
          activeTheme === "light"
            ? { boxShadow: "var(--shadow-severity-selected)" }
            : undefined
        }
      >
        <i className="pi pi-sun text-sm" />
      </button>
      {/* Moon — dark mode */}
      <button
        type="button"
        onClick={() => activeTheme !== "dark" && handleThemeToggle()}
        aria-label="Dark theme"
        title="Dark theme"
        className={`flex cursor-pointer items-center justify-center w-[30px] h-[30px] rounded-full transition-all duration-150 ${
          activeTheme === "dark"
            ? "bg-[var(--card-bg)] border border-[var(--border-default)] text-[var(--text-primary)]"
            : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
        }`}
        style={
          activeTheme === "dark"
            ? { boxShadow: "var(--shadow-severity-selected)" }
            : undefined
        }
      >
        <i className="pi pi-moon text-sm" />
      </button>
    </div>
  );

  /** Icon-only toggle shown on small screens */
  const ThemeToggleIcon = (
    <button
      type="button"
      onClick={handleThemeToggle}
      aria-label={`Switch to ${activeTheme === "dark" ? "light" : "dark"} theme`}
      title={`Switch to ${activeTheme === "dark" ? "light" : "dark"} theme`}
      className="flex cursor-pointer items-center justify-center w-8 h-8 rounded-full border border-[var(--border-default)] bg-[var(--bg-elevated)] text-[var(--text-primary)] transition duration-150 hover:bg-[var(--bg-hover)]"
    >
      <i className={activeTheme === "dark" ? "pi pi-sun text-sm" : "pi pi-moon text-sm"} />
    </button>
  );

  return (
    <div className="flex flex-row items-center justify-between h-16 w-full text-[var(--text-primary)] bg-[var(--nav-main-bg)] border-b border-[var(--nav-header-border)] px-3 md:px-4">

      {/* ── Logo + Title ── */}
      <div className="flex flex-row gap-2.5 items-center min-w-0 ml-2 md:ml-4">
        {/* Logo in dark rounded square */}
        <div className={logoContainerClass}>
          <img src={navbarLogo} alt="Logo" className="h-8 w-auto" />
        </div>

        {/* Title — same sizes as before */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="hidden lg:inline text-xl font-medium whitespace-nowrap text-[var(--text-primary)]">
            CLOUD NOTIFICATION CENTER
          </span>
          <span className="inline lg:hidden text-base font-medium whitespace-nowrap text-[var(--text-primary)]">
            CNC
          </span>

          {/* Env badge */}
          {env === "DEV" && (
            <span
              className={`text-sm md:text-base font-normal leading-none flex-shrink-0 ${
                activeTheme === "dark"
                  ? "text-[var(--text-primary)]"
                  : "text-[var(--text-black)]"
              }`}
            >
              (Dev)
            </span>
          )}
        </div>
      </div>

      {/* ── Right Section ── */}
      <div className="flex flex-row items-center gap-2 md:gap-2.5">

        {/* Theme toggle — pill on md+, icon on sm */}
        <div className="hidden md:flex">{ThemeTogglePill}</div>
        <div className="flex md:hidden">{ThemeToggleIcon}</div>

        {/* Timezone */}
        <div className="hidden md:flex">
          <TimezoneDropdown />
        </div>

        {/* Critical events & Notifications */}
        {hasCriticalEventsAccess && (
          <CriticalEventsPanel activeBtn={activeButton} onSelect={handleActiveButton} />
        )}
        {hasAlertsAccess && (
          <NotificationPanel activeBtn={activeButton} onSelect={handleActiveButton} />
        )}

        {/* Divider */}
        <div className="hidden md:block h-5 w-px bg-[var(--border-default)]" />

        {/* User name + initials avatar */}
        <button
          type="button"
          className="flex items-center gap-2 cursor-pointer rounded-lg px-1.5 py-1 hover:bg-[var(--bg-hover)] transition duration-150"
          onClick={(e) => op.current?.toggle(e)}
        >
          <span className="hidden md:inline text-sm font-medium text-[var(--text-primary)] max-w-[120px] truncate">
            {nameUser}
          </span>
          <Avatar
            size="normal"
            shape="circle"
            className="bg-[var(--bg-elevated)] text-[var(--text-primary)] profile-initials-avatar"
          >
            {initials || "?"}
          </Avatar>
        </button>

        {/* Mobile menu — sm only */}
        <button
          type="button"
          aria-label="More options"
          className="flex md:hidden items-center justify-center w-8 h-8 rounded-lg border border-[var(--border-default)] bg-[var(--bg-elevated)] text-[var(--text-primary)] transition hover:bg-[var(--bg-hover)]"
          onClick={(e) => mobileMenuOp.current?.toggle(e)}
        >
          <i className="pi pi-ellipsis-v text-sm" />
        </button>

        {/* Mobile overflow panel */}
        <OverlayPanel ref={mobileMenuOp} dismissable style={{ minWidth: "220px" }} className="navbar-user-overlay">
          <div className="flex flex-col gap-3 p-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-[var(--text-tertiary)] font-medium">Theme</span>
              {ThemeTogglePill}
            </div>
            <div className="h-px bg-[var(--border-default)] w-full" />
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-[var(--text-tertiary)] font-medium">Timezone</span>
              <TimezoneDropdown />
            </div>
          </div>
        </OverlayPanel>

        {/* User profile dropdown */}
        <OverlayPanel ref={op} dismissable style={{ minWidth: "250px" }} className="navbar-user-overlay">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-[var(--text-tertiary)]">
                Role:{" "}
                <span className="text-[var(--status-info)]">{roleLabel || "—"}</span>
              </p>
            </div>
            <div className="w-12 h-12 rounded-full border border-[var(--border-default)] flex items-center justify-center bg-[var(--bg-elevated)]">
              <i className="pi pi-user text-xl text-[var(--text-primary)]" />
            </div>
          </div>

          <div className="h-px bg-[var(--border-default)] my-3 w-full" />

          <div className="space-y-1.5">
            {hasCriticalEventsAccess && (
              <>
                {buttonOptions.map((item) => (
                  <Button
                    key={item.id}
                    icon={item.icon}
                    className={profileActionClass}
                    onClick={() => handleClick(item.routePath)}
                  >
                    {item.label}
                  </Button>
                ))}
                <div className="h-px bg-[var(--border-default)] my-3 w-full" />
              </>
            )}
            <Button
              onClick={logout}
              icon="pi pi-sign-out"
              className="navbar-user-signout w-full flex items-center gap-2 p-button-text p-button-sm rounded-lg transition duration-150"
            >
              Sign Out
            </Button>
          </div>
        </OverlayPanel>
      </div>
    </div>
  );
}

export default memo(Navbar);
